import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { isChunkLoadError } from '@/shared/utils/chunkLoadError';
import { markAppStale } from '@/shared/utils/appVersionStore';
import { retryChunkLoad } from '@/shared/utils/lazyWithRetry';

const SUPPORTED_LANGUAGES = ['zh-TW', 'en-US'] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// 快取已載入的 ZLF 模組，避免重複載入
const loadedNamespaces = new Set<string>();

// 動態載入所有 ZLF 檔案的 glob import
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const zlfModules: Record<SupportedLanguage, Record<string, () => Promise<any>>> = {
    'zh-TW': import.meta.glob('@/i18n/zh-TW/**/*.zlf'),
    'en-US': import.meta.glob('@/i18n/en-US/**/*.zlf'),
};

/** 閒置預載用：所有語言的翻譯 chunk 載入器（頁面本來就會同時載入兩種語言） */
export function getZlfChunkLoaders(): Array<() => Promise<unknown>> {
    return SUPPORTED_LANGUAGES.flatMap((lang) => Object.values(zlfModules[lang]));
}

interface UseZlfTranslationOptions {
    /**
     * 檔案路徑識別符，對應到 src 下的相對路徑
     * 例如: 'pages/members/MemberListPage'
     */
    filePath: string;
}

interface UseZlfTranslationReturn {
    /** 翻譯函式 */
    t: (key: string, options?: Record<string, unknown>) => string;
    /** 是否已載入完成 */
    ready: boolean;
    /** 切換語言 */
    changeLanguage: (lang: SupportedLanguage) => Promise<void>;
    /** 目前語言 */
    currentLanguage: string;
}

/**
 * 將 ZLF 翻譯註冊到 i18n
 */
function registerZlfTranslations(
    namespace: string,
    lang: SupportedLanguage,
    translations: Record<string, unknown>
): void {
    i18n.addResourceBundle(lang, namespace, translations, true, true);
}

/**
 * 頁面/元件翻譯 Hook
 * 自動載入對應的 .zlf 檔案並註冊為 i18n namespace
 *
 * @example
 * // 在 MemberListPage.tsx 中使用
 * const { t, ready } = usePageTranslation({
 *   filePath: 'pages/members/MemberListPage'
 * });
 *
 * // 使用翻譯
 * t('title')  // 從 MemberListPage.zlf 取得
 */
export function usePageTranslation({
    filePath,
}: UseZlfTranslationOptions): UseZlfTranslationReturn {
    const namespace = `zlf:${filePath}`;
    const [isLoaded, setIsLoaded] = useState(loadedNamespaces.has(namespace));
    const { t: i18nT, i18n: i18nInstance } = useTranslation();

    useEffect(() => {
        // 如果已經載入過，直接返回
        if (loadedNamespaces.has(namespace)) {
            setIsLoaded(true);
            return;
        }

        let isMounted = true;

        async function loadZlfFiles() {
            let hasChunkFailure = false;
            const loadPromises = SUPPORTED_LANGUAGES.map(async (lang) => {
                // 建構 ZLF 檔案路徑（Vite glob 返回 /src/ 格式）
                const zlfPath = `/src/i18n/${lang}/${filePath}.zlf`;
                const loader = zlfModules[lang][zlfPath];

                if (loader) {
                    try {
                        const module = await retryChunkLoad(loader);
                        registerZlfTranslations(namespace, lang, module.default);
                    } catch (error) {
                        // 換版後舊的翻譯 chunk 已不存在：不丟錯，沿用已載入的文字或 key，並提示重新整理
                        if (isChunkLoadError(error)) {
                            hasChunkFailure = true;
                            markAppStale();
                        }
                        console.debug(`[ZLF] Failed to load ${lang}/${filePath}.zlf:`, error);
                    }
                } else {
                    console.debug(`[ZLF] File not found: ${lang}/${filePath}.zlf`);
                }
            });

            await Promise.all(loadPromises);

            if (isMounted) {
                // 失敗不快取，之後再進入這個頁面時還有機會重新載入
                if (!hasChunkFailure) loadedNamespaces.add(namespace);
                setIsLoaded(true);
            }
        }

        loadZlfFiles();

        return () => {
            isMounted = false;
        };
    }, [filePath, namespace]);

    // 包裝 t 函式，優先使用 ZLF namespace，fallback 到舊的 translation
    const t = useCallback(
        (key: string, options?: Record<string, unknown>): string => {
            // 先嘗試從 ZLF namespace 取得
            const zlfResult = i18n.t(key, { ns: namespace, ...options });

            // 如果結果是 key 本身（找不到翻譯），嘗試從舊的 translation namespace
            if (zlfResult === key) {
                return String(i18nT(key, options as never));
            }

            return String(zlfResult);
        },
        [namespace, i18nT]
    );

    const changeLanguage = useCallback(
        async (lang: SupportedLanguage) => {
            await i18nInstance.changeLanguage(lang);
        },
        [i18nInstance]
    );

    return {
        t,
        ready: isLoaded,
        changeLanguage,
        currentLanguage: i18nInstance.language,
    };
}

/**
 * 元件翻譯 Hook
 * 與 usePageTranslation 相同邏輯，語義上區分為元件使用
 *
 * @example
 * // 在 ConfirmDialog.tsx 中使用
 * const { t } = useComponentTranslation({
 *   filePath: 'components/common/ConfirmDialog'
 * });
 */
export function useComponentTranslation(
    options: UseZlfTranslationOptions
): UseZlfTranslationReturn {
    return usePageTranslation(options);
}
